import { useCallback, useState } from 'react';
import DrawerFlow, { DrawerFlowStep } from 'src/UI/drawer-flow/DrawerFlow';
import { SectionSplittingLevel, TemplateCreationData } from 'src/company/templates/types';
import { initialTemplateCreationData, TemplateCreationProvider } from './TemplateCreationProvider';
import EditSectionInstructionsStep from './steps/EditSectionInstructionsStep';
import FinalizeTemplateStep from './steps/FinalizeTemplateStep';
import { UploadedTemplate } from 'src/data-capture/hooks/templates/useTemplates';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import { useTemplate } from 'src/company/templates/hooks/useTemplate';
import PageLoader from 'src/UI/loader/PageLoader';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useMarkdownImageCollection } from 'src/company/templates/hooks/useMarkdownImageCollection';
import { MarkdownImageCollection } from 'src/company/templates/hooks/useConvertToMarkdownMutation';

type TemplateUpdateFlowProps = {
  opened: boolean;
  close: () => void;
  templateId: string;
  company: Company;
};

const steps: DrawerFlowStep<TemplateCreationData>[] = [
  {
    title: 'Template details',
    screens: [
      {
        component: FinalizeTemplateStep,
        allowVerticalOverflow: true,
      },
    ],
  },
  {
    title: 'Edit sections',
    screens: [
      {
        component: EditSectionInstructionsStep,
        allowVerticalOverflow: true,
      },
    ],
  },
];

export default function TemplateUpdateFlow({
  opened,
  close,
  company,
  templateId,
}: TemplateUpdateFlowProps) {
  const {
    template,
    isTemplateLoading,
    isTemplateFetchedAfterMount,
    isTemplateError,
    templateError,
  } = useTemplate({
    companyId: company.id,
    templateId: templateId,
  });

  const {
    collection,
    isLoading: isCollectionLoading,
    isError: isCollectionError,
    error: collectionError,
    isFetched: isCollectionFetched,
  } = useMarkdownImageCollection({
    companyId: company.id,
    collectionId: template?.markdownImageCollectionId || '',
    enabled: !!template?.markdownImageCollectionId,
  });

  if (isTemplateError || isCollectionError) {
    return <GenericErrorPage error={templateError || collectionError} />;
  }

  // The editor seeds its state from the template once, so a copy cached before the last save would
  // reopen with that save undone; wait for the template as it is now.
  if (
    isTemplateLoading ||
    !isTemplateFetchedAfterMount ||
    !template ||
    (!!template?.markdownImageCollectionId && isCollectionLoading) ||
    (template.markdownImageCollectionId && !isCollectionFetched)
  ) {
    return <PageLoader />;
  }

  return (
    <TemplateUpdateFlowWithTemplate
      template={template}
      company={company}
      opened={opened}
      close={close}
      templateId={templateId}
      collection={collection}
    />
  );
}

function TemplateUpdateFlowWithTemplate({
  template,
  company,
  opened,
  close,
  collection,
}: TemplateUpdateFlowProps & { template: UploadedTemplate; collection?: MarkdownImageCollection }) {
  const [data, setData] = useState<TemplateCreationData>({
    templateType: template.templateType,
    templateName: template.displayName,
    templateDescription: template.description,
    markdownSplitLevel: 2,
    sectionSplittingLevel: (template.sectionSplittingLevel ?? null) as SectionSplittingLevel | null,
    templateSections: template.sections.map(section => ({
      id: section.id,
      sectionHeader: section.sectionHeader,
      sectionHeaderLevel: section.sectionHeaderLevel,
      templatedMarkdown: section.templateText,
      isSectionInPopulatedMarkdown: true,
      populatedMarkdown: section.templateText,
      sectionInstructions: section.sectionInstructions,
      preserveAsIs: section.preserveAsIs,
      isContentComplete: !!section.isInstructionsComplete,
    })),
    createdTemplate: template,
    extractedImagesCollection: collection || null,
    company,
    meetingNoteTense: template.meetingNoteTense ?? 'THIRD_PERSON',
    eligibleForSummarisation: template.eligibleForSummarisation,
  });

  const onSuccess = useCallback(() => {
    close();
    // TODO: Show success notification or refresh templates list
  }, [close]);

  return (
    <TemplateCreationProvider company={company}>
      <DrawerFlow
        opened={opened}
        close={close}
        title="Update template"
        steps={steps}
        initialData={{ ...initialTemplateCreationData, company }}
        data={data}
        setData={setData}
        onSuccess={onSuccess}
        drawerProps={{ closeOnEscape: false }}
      />
    </TemplateCreationProvider>
  );
}
